import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
  Avatar,
  CardHeader,
  LinearProgress
} from "@mui/material";
import {
  Add,
  Visibility,
  Search,
  FilterList,
  Refresh,
  Download,
  MoreVert,
  LocalHospital,
  MeetingRoom,
  Inventory,
  CalendarToday,
  TrendingUp,
  BarChart,
  Receipt,
  Assignment
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  getLinenIssues,
  getLinenIssueStats,
  getWardsAndFloors,
  getLinenItems
} from "../../services/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Enhanced StatsCard component for summary statistics
const StatsCard = ({ title, value, subtitle, color = "primary", icon: Icon, ...props }) => (
  <Card 
    {...props}
    sx={{ 
      borderRadius: 3, 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      ...props.sx 
    }}
  >
    <CardContent sx={{ p: 3, textAlign: 'center' }}>
      <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
        <Icon sx={{ fontSize: 32, color: 'white' }} />
      </Avatar>
      <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export const LinenIssueList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [stats, setStats] = useState({});
  const [wards, setWards] = useState([]);
  const [linenItems, setLinenItems] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [wardFilter, setWardFilter] = useState("all");
  const [itemFilter, setItemFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("month");
  
  // Dialogs and menus
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIssue, setMenuIssue] = useState(null);

  const periodOptions = [
    { value: "today", label: "Today" },
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
    { value: "custom", label: "Custom Range" }
  ];

  useEffect(() => {
    loadData();
  }, [periodFilter]);

  useEffect(() => {
    filterIssues();
  }, [issues, searchTerm, wardFilter, itemFilter, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      let dateRange = {};
      if (periodFilter !== "custom") {
        dateRange = { period: periodFilter };
      } else if (dateFrom && dateTo) {
        dateRange = {
          dateFrom: dateFrom.format('YYYY-MM-DD'),
          dateTo: dateTo.format('YYYY-MM-DD')
        };
      }

      const [issuesRes, statsRes, wardsRes, itemsRes] = await Promise.all([
        getLinenIssues({ ...dateRange }),
        getLinenIssueStats({ period: periodFilter }),
        getWardsAndFloors(),
        getLinenItems({ status: "active" })
      ]);

      setIssues(issuesRes.data?.issues || []);
      setStats(statsRes.data || {});
      setWards(wardsRes.data?.wards || []);
      setLinenItems(itemsRes.data?.items || []);
      
    } catch (err) {
      console.error("Load data error:", err);
      enqueueSnackbar("❌ Failed to load issues data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filterIssues = () => {
    let filtered = [...issues];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(issue =>
        issue.item?.name?.toLowerCase().includes(term) ||
        issue.ward?.name?.toLowerCase().includes(term) ||
        issue.performedBy?.name?.toLowerCase().includes(term) ||
        issue.notes?.toLowerCase().includes(term)
      );
    }

    // Ward filter
    if (wardFilter !== "all") {
      filtered = filtered.filter(issue => issue.ward?._id === wardFilter);
    }

    // Item filter
    if (itemFilter !== "all") {
      filtered = filtered.filter(issue => issue.item?._id === itemFilter);
    }

    // Date filter for custom range
    if (periodFilter === "custom" && dateFrom && dateTo) {
      filtered = filtered.filter(issue => {
        const issueDate = dayjs(issue.createdAt);
        return issueDate.isAfter(dateFrom.subtract(1, 'day')) && 
               issueDate.isBefore(dateTo.add(1, 'day'));
      });
    }

    // Sort by latest first
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredIssues(filtered);
  };

  const handleMenuOpen = (event, issue) => {
    setMenuAnchor(event.currentTarget);
    setMenuIssue(issue);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuIssue(null);
  };

  const handleView = (issue) => {
    setSelectedIssue(issue);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleExport = () => {
    // Simple CSV export
    const headers = ["Date", "Ward", "Room", "Item", "Quantity", "Unit", "Issued By", "Notes"];
    const csvData = filteredIssues.map(issue => [
      dayjs(issue.createdAt).format('DD/MM/YYYY HH:mm'),
      issue.ward?.name || 'Unknown Ward',
      issue.room?.room_no || 'Not specified',
      issue.item?.name || 'Unknown Item',
      issue.quantity,
      issue.item?.unit || 'pcs',
      issue.performedBy?.name || issue.performedBy || 'System',
      issue.notes || '-'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `linen-issues-${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("📊 Data exported successfully", { variant: "success" });
  };

  const getWardName = (wardId) => {
    if (!wardId) return "Unknown Ward";
    if (typeof wardId === 'object') return wardId.name;
    const ward = wards.find(w => w._id === wardId);
    return ward?.name || wardId;
  };

  const getItemName = (itemId) => {
    if (!itemId) return "Unknown Item";
    if (typeof itemId === 'object') return itemId.name;
    const item = linenItems.find(i => i._id === itemId);
    return item?.name || itemId;
  };

  const getSummaryStats = () => {
    const total = filteredIssues.length;
    const totalQuantity = filteredIssues.reduce((sum, issue) => sum + (issue.quantity || 0), 0);
    const uniqueWards = [...new Set(filteredIssues.map(issue => issue.ward?._id || issue.ward).filter(Boolean))].length;
    const uniqueItems = [...new Set(filteredIssues.map(issue => issue.item?._id || issue.item).filter(Boolean))].length;

    return { total, totalQuantity, uniqueWards, uniqueItems };
  };

  const currentStats = getSummaryStats();

  if (loading && issues.length === 0) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Issue History...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing issue records and statistics
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
        {/* Header */}
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3
        }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Receipt sx={{ fontSize: 48, color: 'white' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography 
                  variant="h2" 
                  fontWeight={800}
                  sx={{ 
                    color: 'white',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    mb: 1
                  }}
                >
                  Linen Issue History
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white', 
                    opacity: 0.9,
                    fontSize: '1.3rem'
                  }}
                >
                  Track and analyze linen distribution across wards
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<Add sx={{ fontSize: 28 }} />}
                  onClick={() => navigate("/hospital/housekeeping-and-linen-management/linen/linen-issue")}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  New Issue
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total Issues"
              value={currentStats.total}
              subtitle="Current period"
              color="primary"
              icon={Assignment}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Items Issued"
              value={currentStats.totalQuantity}
              subtitle="Total quantity"
              color="success"
              icon={Inventory}
              sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Wards"
              value={currentStats.uniqueWards}
              subtitle="Unique wards"
              color="info"
              icon={LocalHospital}
              sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Linen Types"
              value={currentStats.uniqueItems}
              subtitle="Different items"
              color="warning"
              icon={BarChart}
              sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}
            />
          </Grid>
        </Grid>

        {/* Global Statistics */}
        {stats.topItems && stats.topItems.length > 0 && (
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title={
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  📈 Top Issued Items
                </Typography>
              }
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            />
            <CardContent>
              <Grid container spacing={3}>
                {stats.topItems.slice(0, 4).map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={item._id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: 'primary.main',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CardContent>
                        <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 700, mb: 1 }}>
                          {item.itemName || item.itemCode || `Item ${index + 1}`}
                        </Typography>
                        <Typography variant="h4" color="primary.main" sx={{ fontSize: '2rem', fontWeight: 800, mb: 1 }}>
                          {item.totalQuantity}
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                          {item.issueCount} transactions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Filters Card */}
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <CardHeader
            title={
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
                🔍 Filter Issues
              </Typography>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search by item, ward, or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "primary.main", fontSize: 28 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '1.1rem',
                      '& input': {
                        padding: '12px 14px',
                        fontSize: '1.1rem'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Period</InputLabel>
                  <Select
                    value={periodFilter}
                    label="Period"
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    {periodOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {periodFilter === "custom" && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="From Date"
                      value={dateFrom}
                      onChange={(newValue) => setDateFrom(newValue)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          size: "medium",
                          InputProps: {
                            sx: { fontSize: '1.1rem' }
                          },
                          InputLabelProps: { sx: { fontSize: '1.1rem' } }
                        } 
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="To Date"
                      value={dateTo}
                      onChange={(newValue) => setDateTo(newValue)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          size: "medium",
                          InputProps: {
                            sx: { fontSize: '1.1rem' }
                          },
                          InputLabelProps: { sx: { fontSize: '1.1rem' } }
                        } 
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Ward</InputLabel>
                  <Select
                    value={wardFilter}
                    label="Ward"
                    onChange={(e) => setWardFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <LocalHospital sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Wards</MenuItem>
                    {wards.map(ward => (
                      <MenuItem key={ward._id} value={ward._id} sx={{ fontSize: '1.1rem' }}>
                        {ward.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Item</InputLabel>
                  <Select
                    value={itemFilter}
                    label="Item"
                    onChange={(e) => setItemFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Inventory sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Items</MenuItem>
                    {linenItems.map(item => (
                      <MenuItem key={item._id} value={item._id} sx={{ fontSize: '1.1rem' }}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={loadData} 
                      size="large"
                      sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.dark' }
                      }}
                    >
                      <Refresh sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export CSV">
                    <IconButton 
                      onClick={handleExport} 
                      size="large" 
                      disabled={filteredIssues.length === 0}
                      sx={{ 
                        backgroundColor: 'success.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'success.dark' },
                        '&.Mui-disabled': { backgroundColor: 'grey.400' }
                      }}
                    >
                      <Download sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Issues Table */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Receipt sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  Issue Records
                </Typography>
                <Chip 
                  label={filteredIssues.length} 
                  color="primary" 
                  size="medium"
                  sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 60 }}
                />
              </Box>
            }
            action={
              (searchTerm || wardFilter !== "all" || itemFilter !== "all") && (
                <Chip
                  label="Filters Active"
                  color="primary"
                  size="medium"
                  onDelete={() => {
                    setSearchTerm("");
                    setWardFilter("all");
                    setItemFilter("all");
                    setPeriodFilter("month");
                  }}
                  sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                />
              )
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            {filteredIssues.length === 0 ? (
              <Alert 
                severity="info" 
                sx={{ 
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  '& .MuiAlert-message': { fontSize: '1.1rem' }
                }}
              >
                {issues.length === 0 
                  ? "📦 No issue records found. Create your first issue to get started." 
                  : "🔍 No issues match your current filters."}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📅 Date & Time
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏢 Ward
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🚪 Room
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📦 Item
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🔢 Quantity
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        👤 Issued By
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📝 Notes
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🛠️ Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIssues.map((issue) => (
                      <TableRow 
                        key={issue._id} 
                        hover
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Stack>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {dayjs(issue.createdAt).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {dayjs(issue.createdAt).format('HH:mm')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getWardName(issue.ward)}
                            size="medium"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '1rem', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          {issue.room ? (
                            <Chip
                              icon={<MeetingRoom sx={{ fontSize: 20 }} />}
                              label={issue.room.room_no}
                              size="medium"
                              variant="outlined"
                              sx={{ fontSize: '1rem', fontWeight: 600 }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                              Not specified
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {getItemName(issue.item)}
                            </Typography>
                            {issue.item?.code && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {issue.item.code}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'primary.main' }}>
                              {issue.quantity}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                              {issue.item?.unit || 'pcs'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {issue.performedBy?.name || issue.performedBy || 'System'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={issue.notes || "No notes"} placement="top">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                color: issue.notes ? 'text.primary' : 'text.secondary',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {issue.notes || "-"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="large"
                              onClick={() => handleView(issue)}
                              sx={{ 
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': { backgroundColor: 'primary.dark' }
                              }}
                            >
                              <Visibility sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* View Issue Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            py: 3
          }}>
            <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
              📋 Issue Details
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {selectedIssue && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📍 Location Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Ward</Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                        {getWardName(selectedIssue.ward)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Room</Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                        {selectedIssue.room ? selectedIssue.room.room_no : "Not specified"}
                      </Typography>
                      {selectedIssue.room && (
                        <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                          {selectedIssue.room.bed_count} beds, {selectedIssue.room.occupied_beds} occupied
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📄 Issue Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Date & Time</Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                        {dayjs(selectedIssue.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Issued By</Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                        {selectedIssue.performedBy?.name || selectedIssue.performedBy || 'System'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📦 Item Details
                  </Typography>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Item Name</Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {getItemName(selectedIssue.item)}
                          </Typography>
                          {selectedIssue.item?.code && (
                            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                              Code: {selectedIssue.item.code}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Category</Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedIssue.item?.category || "Uncategorized"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Quantity</Typography>
                          <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 800, color: 'primary.main' }}>
                            {selectedIssue.quantity} {selectedIssue.item?.unit || 'pcs'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>Available Before</Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedIssue.beforeQty || 'N/A'} {selectedIssue.item?.unit || 'pcs'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                {selectedIssue.notes && (
                  <Grid item xs={12}>
                    <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                      📝 Notes
                    </Typography>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedIssue.notes}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setViewDialogOpen(false)}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <MenuItem onClick={() => handleView(menuIssue)} sx={{ fontSize: '1.1rem' }}>
            <Visibility sx={{ mr: 1, fontSize: 24 }} /> View Details
          </MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
};